/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  CSSProperties,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { fromJS } from 'immutable';
import classNames from 'classnames';
import { WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import type { NodeVacancyEntity } from '../../../../types/nodes';
import { IconList, Button } from '../../../../components';
import { IconListItem } from '../../../../components/IconList';

interface NodeVacancyDetailsProps extends React.HTMLProps<React.HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<NodeVacancyEntity>;
  whatsappLink: string;
  onVacancyApply: () => void;
}

function useCollapsible(ref: React.RefObject<HTMLElement>) {
  const MAX_COLLAPSED_HEIGHT = '130px';
  const TRANSITION_DURATION = 300;

  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const [hasResized, setHasResized] = useState(false);

  const [shouldRecalculateMaxHeight, setShouldRecalculateMaxHeight] = useState(false);

  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    function handleResize() {
      setHasResized(true);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (ref.current && !maxHeight) {
      setMaxHeight(ref.current.getBoundingClientRect().height);
      setIsExpanded(false);
    }
  }, [maxHeight, ref]);

  /**
   * When the page is resized, we need to update the maximum height of the expanded panel.
   * Because we don't have access to the maximum height prior to expanding the panel, we use
   * the document's height as a temporary solution.
   *
   * This means the panel will briefly expand very fast once. Once transitioned, we recalculate
   * the new maximum height so that the animation can be smooth again (that is, until the user
   * resizes the window again.)
   */
  useEffect(() => {
    if (isExpanded && hasResized) {
      setMaxHeight(document.documentElement.scrollHeight);
      setHasResized(false);

      setTimeout(() => {
        setShouldRecalculateMaxHeight(true);
      }, TRANSITION_DURATION);
    }
  }, [isExpanded, hasResized, ref]);

  useEffect(() => {
    if (shouldRecalculateMaxHeight && ref.current) {
      setMaxHeight(ref.current.getBoundingClientRect().height);

      setShouldRecalculateMaxHeight(false);
    }
  }, [shouldRecalculateMaxHeight, ref]);

  const style = React.useMemo<CSSProperties>(() => {
    const transitionDuration = `${TRANSITION_DURATION / 1000}s`;

    if (!maxHeight) {
      return {
        transitionDuration,
      };
    }

    if (maxHeight && isExpanded) {
      return {
        maxHeight: `${maxHeight}px`,
        transitionDuration,
      };
    }

    return {
      maxHeight: MAX_COLLAPSED_HEIGHT,
      transitionDuration,
    };
  }, [isExpanded, maxHeight]);

  return {
    isExpanded,
    setIsExpanded,
    style,
  };
}

const NodeVacancyDetails: React.FC<NodeVacancyDetailsProps> = (props) => {
  const { entity, whatsappLink, onVacancyApply } = props;

  const { t } = useTranslation();

  const offersListRef = useRef<HTMLDivElement>(null);

  const requirementsListRef = useRef<HTMLDivElement>(null);

  const node = entity.toJS();

  const {
    isExpanded: isOffersListOpen,
    setIsExpanded: setIsOffersListOpen,
    style: offersListStyle,
  } = useCollapsible(offersListRef);

  const {
    isExpanded: isRequirementsListOpen,
    setIsExpanded: setIsRequirementsListOpen,
    style: requirementsListStyle,
  } = useCollapsible(requirementsListRef);


  const dateFormat = new Intl.DateTimeFormat('nl-NL', { month: 'long', day: 'numeric' });
  const validThrough = entity.getIn(['field_valid_through', 0, 'value'], null);
  const validThroughFormatted = (
    validThrough === null ? null : dateFormat.format(Date.parse(validThrough))
  );
  const createdDate = entity.getIn(['created', 0, 'value'], new Date());
  const publicationDate = dateFormat.format(Date.parse(entity.getIn(['field_publication_date', 0, 'value'], createdDate)));

  const employmentLocation = String(entity.getIn(['field_address', 0, 'title']));
  const employmentType = String(entity.getIn(['field_employment_type', 0, 'title']));

  const highlightList: IconListItem[] = [{
    key: 'highlight:location',
    text: employmentLocation,
    icon: 'location_on',
  }, {
    key: 'highlight:employment',
    text: employmentType,
    icon: 'work',
  }, {
    key: 'highlight:time',
    text: `Geplaatst op ${publicationDate}${(validThroughFormatted === null ? '' : ` - Solliciteren kan tot ${validThroughFormatted}`)}`,
    icon: 'schedule',
  }];

  const numberFormat = new Intl.NumberFormat('nl-NL');
  const minSalary = numberFormat.format(entity.getIn(['field_min_salary', 0, 'value'], null));
  const maxSalary = numberFormat.format(entity.getIn(['field_max_salary', 0, 'value'], null));
  const salary = entity.getIn(['field_salary', 0, 'value'], null);

  const salaryType = node.field_salary_type[0] ? node.field_salary_type[0].entity?.name[0].value.toLowerCase() : '';
  const hourType = node.field_hour_type[0] ? node.field_hour_type[0].entity?.name[0].value : null;

  const minHours = numberFormat.format(entity.getIn(['field_minimum_contracted_hours', 0, 'value'], null));
  const maxHours = numberFormat.format(entity.getIn(['field_maximum_contracted_hours', 0, 'value'], null));

  const offersList: IconListItem[] = [];

  if (minSalary && maxSalary !== '0' && salaryType && hourType) {
    offersList.push({
      key: 'offer:salary',
      text: `€${minSalary} - €${maxSalary} ${salaryType} ${hourType}`,
      icon: 'money_bag',
    });
  } else if (salary) {
    offersList.push({
      key: 'offer:salary',
      text: salary,
      icon: 'money_bag',
    });
  }

  offersList.push({
    key: 'offer:hours',
    text: minHours === maxHours || !minHours
      ? `${maxHours}-urige werkweek`
      : `${minHours} - ${maxHours}-urige werkweek`,
    icon: 'schedule',
  });

  node.field_what_do_we_offer.forEach((term) => {
    offersList.push({
      key: `offer:${term.target_id}`,
      text: term.entity.name[0].value ?? '',
      icon: fromJS(term.entity).getIn(['field_offer_icons', 0, 'entity', 'field_vacancy_icon_type', 0, 'value'], 'check') as string,
    });
  });

  return (
    <div id="vacancy-details" className={classNames('Vacancy--details')}>
      <div className="Vacancy--details-inner">
        <div className="Vacancy--details-header">
          <div className="Vacancy--details-header-top">
            <h1 className="small Vacancy--details-header-heding">{entity.getIn(['title', 0, 'value'])}</h1>
            <VacancyDetailsCta
              whatsappLink={whatsappLink}
              onVacancyApply={onVacancyApply}
              onlyShowOn="desktop"
            />
          </div>
          <IconList listItems={highlightList} large />
          <VacancyDetailsCta
            whatsappLink={whatsappLink}
            onVacancyApply={onVacancyApply}
            onlyShowOn="mobile"
          />
        </div>
      </div>
      <div
        style={offersListStyle}
        className={classNames('Vacancy--details-inner', 'Vacancy--details-inner__collapsible', 'Vacancy--details-inner__collapsible-offers-list', { collapsed: !isOffersListOpen })}
        ref={offersListRef}
      >
        <button
          type="button"
          className={classNames('Vacancy--details-toggle', 'Vacancy--details-toggle-vacancy-list')}
          onClick={() => setIsOffersListOpen(!isOffersListOpen)}
        >
          <span className={classNames('icon', isOffersListOpen ? 'icon-keyboard_arrow_right' : 'icon-keyboard_arrow_down')}>toggle</span>
        </button>
        <h2 className="h4 Vacancy--details-inner__collapsible-heading">Wat bieden wij?</h2>
        <IconList listItems={offersList} />
      </div>
      <div
        ref={requirementsListRef}
        style={requirementsListStyle}
        className={classNames('Vacancy--details-inner', 'Vacancy--details-inner__collapsible', 'Vacancy--details-inner__collapsible-required-list', { collapsed: !isRequirementsListOpen })}
      >
        <button
          type="button"
          className={classNames('Vacancy--details-toggle', 'Vacancy--details-toggle-required-list')}
          onClick={() => setIsRequirementsListOpen(!isRequirementsListOpen)}
        >
          <span className={classNames('icon', isRequirementsListOpen ? 'icon-keyboard_arrow_right' : 'icon-keyboard_arrow_down')}>toggle</span>
        </button>
        <h2 className="h4 Vacancy--details-inner__collapsible-heading">Wat is vereist?</h2>
        <WysiwygField
          html={node.field_skills[0]?.processed}
        />
      </div>
    </div>
  );
};

function VacancyDetailsCta({
  whatsappLink,
  onVacancyApply,
  onlyShowOn,
}: {
  whatsappLink?: string
  onVacancyApply: () => void
  onlyShowOn: 'mobile' | 'desktop'
}) {
  const { t } = useTranslation();

  const classes = classNames(
    'Vacancy--details-cta',
    `Vacancy--details-cta--${onlyShowOn}`,
  );

  return (
    <div className={classes}>
      <Button
        onClick={() => onVacancyApply()}
      >
        {t('apply_vacancy')}
      </Button>
      {whatsappLink && (
      <Button
        href={whatsappLink}
        title={t('whatsapp_link.long')}
        icon="whatsapp"
        type="secondary"
      >
        {t('whatsapp_link.short')}
      </Button>
      )}
    </div>
  );
}

export default NodeVacancyDetails;
